import {useCallback, useState} from 'react'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {ConfirmationDialogInputs, SocialMutationResponse, StarState} from '../types'
import {parseStarCount} from '../utils/parse-star-count'
import {userListsQueryKey} from './use-user-lists-query'

interface UseStarMutationProps {
  /** URL path to star the repository */
  starPath: string
  /** URL path to unstar the repository */
  unstarPath: string
  /** Whether the repository is initially starred */
  initialStarred: boolean
  /** Initial star count for the repository */
  initialCount: number
  /** API path for user lists, used to invalidate the lists cache after unstar */
  listsApiPath: string
  /** Invoked after a successful unstar so callers can sync related UI state (e.g. close a panel) */
  onUnstarSuccess?: () => void
}

interface UseStarMutationResult {
  /** Current starred state */
  starred: boolean
  /** Current star count */
  starCount: number
  /** Toggle the star state with optimistic update */
  toggleStar: () => void
  /** Information needed to render an unstar confirmation dialog */
  pendingUnstarConfirmation: ConfirmationDialogInputs | null
  /** Submit the confirmed unstar request */
  confirmUnstar: () => void
  /** Dismiss the pending unstar confirmation dialog */
  dismissUnstarConfirmation: () => void
  /** Whether the button should show a loading/disabled state */
  isLoading: boolean
  /** Synchronize star state from related UI flows such as list updates */
  syncExternalState: (next: StarState) => void
}

interface StarMutationResult {
  status: number
  ok: boolean
  payload: SocialMutationResponse | null
}

function getConfirmationInputs(payload: SocialMutationResponse | null): ConfirmationDialogInputs | null {
  const inputs = payload?.confirmationDialog?.inputs
  if (!inputs?.repoNameWithOwner || !inputs?.listsWithCount) return null

  return {
    repoNameWithOwner: inputs.repoNameWithOwner,
    listsWithCount: inputs.listsWithCount,
  }
}

async function parseResponsePayload(response: Response): Promise<SocialMutationResponse | null> {
  try {
    return (await response.json()) as SocialMutationResponse
  } catch {
    return null
  }
}

/**
 * Hook to manage star/unstar mutations with optimistic updates.
 *
 * The 409 confirmation flow keeps isLoading=true from the initial click
 * through the confirmation dialog until the confirmed unstar completes,
 * so the button stays in a loading/disabled state the whole time.
 */
export function useStarMutation({
  starPath,
  unstarPath,
  initialStarred,
  initialCount,
  listsApiPath,
  onUnstarSuccess,
}: UseStarMutationProps): UseStarMutationResult {
  const [starred, setStarred] = useState(initialStarred)
  const [starCount, setStarCount] = useState(initialCount)
  const [pendingUnstarConfirmation, setPendingUnstarConfirmation] = useState<ConfirmationDialogInputs | null>(null)
  const queryClient = useQueryClient()

  const starToggleMutation = useMutation<StarMutationResult, Error, boolean, {previous: StarState}>({
    mutationFn: async (newStarred: boolean) => {
      const path = newStarred ? starPath : unstarPath
      const response = await verifiedFetch(path, {method: 'POST'})
      const payload = await parseResponsePayload(response)
      return {status: response.status, ok: response.ok, payload}
    },

    onMutate: newStarred => {
      const previous = {starred, starCount}
      if (newStarred) {
        // Only optimistically update when starring — unstarring waits
        // for the server since it may require confirmation (409)
        setStarred(true)
        setStarCount(prev => prev + 1)
      }
      return {previous}
    },

    onSuccess: (data, newStarred, context) => {
      if (!context) return

      if (!newStarred && data.status === 409) {
        setPendingUnstarConfirmation(getConfirmationInputs(data.payload))
        return
      }

      if (!data.ok) {
        setStarred(context.previous.starred)
        setStarCount(context.previous.starCount)
        return
      }

      const count = parseStarCount(data.payload?.count)

      if (newStarred) {
        // Starred successfully — sync server count
        setStarCount(count ?? context.previous.starCount + 1)
      } else {
        // Unstarred successfully — now apply the state change
        setStarred(false)
        setStarCount(count ?? context.previous.starCount - 1)
        setPendingUnstarConfirmation(null)
        onUnstarSuccess?.()
      }
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        setStarred(context.previous.starred)
        setStarCount(context.previous.starCount)
      }
    },

    onSettled: (data, _error, newStarred) => {
      // Only reset on actual unstar, not when a 409 triggers the confirmation dialog.
      // resetQueries clears cached data so the panel shows a loading state
      // instead of stale list membership on next open.
      if (!newStarred && data?.status !== 409) {
        void queryClient.resetQueries({queryKey: userListsQueryKey(listsApiPath)})
      }
    },
  })

  const confirmUnstarMutation = useMutation<StarMutationResult, Error, void, {previous: StarState}>({
    mutationFn: async () => {
      const response = await verifiedFetch(unstarPath, {
        method: 'POST',
        body: new URLSearchParams({confirm: 'true'}),
      })
      const payload = await parseResponsePayload(response)
      return {status: response.status, ok: response.ok, payload}
    },

    onMutate: () => {
      const previous = {starred, starCount}
      setPendingUnstarConfirmation(null)
      setStarred(false)
      setStarCount(prev => prev - 1)
      return {previous}
    },

    onSuccess: (data, _variables, context) => {
      if (!context) return

      if (!data.ok) {
        setStarred(true)
        setStarCount(context.previous.starCount)
        if (data.status === 409) {
          setPendingUnstarConfirmation(getConfirmationInputs(data.payload))
        }
        return
      }

      const count = parseStarCount(data.payload?.count)
      setStarCount(count ?? context.previous.starCount - 1)
      onUnstarSuccess?.()
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        setStarred(true)
        setStarCount(context.previous.starCount)
      }
    },

    onSettled: () => {
      void queryClient.resetQueries({queryKey: userListsQueryKey(listsApiPath)})
    },
  })

  // Show loading from initial click through 409 confirmation until resolved
  const isMutating = starToggleMutation.isPending || confirmUnstarMutation.isPending
  const isLoading = isMutating || pendingUnstarConfirmation !== null

  const {mutate: toggleStarMutate} = starToggleMutation
  const {mutate: confirmUnstarMutate} = confirmUnstarMutation

  const toggleStar = useCallback(() => {
    if (isLoading) return
    toggleStarMutate(!starred)
  }, [isLoading, toggleStarMutate, starred])

  const confirmUnstar = useCallback(() => {
    if (isMutating || !pendingUnstarConfirmation) return
    confirmUnstarMutate()
  }, [confirmUnstarMutate, isMutating, pendingUnstarConfirmation])

  const dismissUnstarConfirmation = useCallback(() => {
    setPendingUnstarConfirmation(null)
  }, [])

  const syncExternalState = useCallback((next: StarState) => {
    setStarred(next.starred)
    setStarCount(next.starCount)
  }, [])

  return {
    starred,
    starCount,
    toggleStar,
    pendingUnstarConfirmation,
    confirmUnstar,
    dismissUnstarConfirmation,
    isLoading,
    syncExternalState,
  }
}
